/**
 * The call on hold, shown above the call in front.
 *
 * It stays on screen for as long as somebody is listening to hold music,
 * whatever the call in front is doing - talking, ending, or showing its
 * after-call form - because a customer nobody can see on screen is a customer
 * left on hold for good. "Take back" is the one action: it swaps the two calls
 * when the call in front can be held, and otherwise waits for that call to end.
 */
import { Pause, PhoneIncoming } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import type { HeldView } from "./useCallWaiting";

export function HeldStrip({
  held,
  canTakeBack,
  onTakeBack,
}: {
  held: HeldView;
  canTakeBack: boolean;
  onTakeBack: () => void;
}) {
  const t = useT();
  const number = held.number ? formatPhone(held.number) : "";
  const title = held.name || number || t("calls.incoming.unknownCaller");
  const note = held.error
    ? t(`calls.waiting.error.${held.error}`)
    : canTakeBack
      ? null
      : t("calls.waiting.takeBackLater");

  return (
    <div className="flex flex-none flex-col gap-1.5 border-b border-border/60 px-5 py-3" role="status">
      <div className="flex items-center gap-3">
        <Pause className="size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("calls.waiting.onHold")}
          </p>
          <p className="truncate text-[13.5px] font-semibold">
            {title}
            {held.name && number ? (
              <span className="ml-2 font-normal tabular-nums text-muted-foreground">{number}</span>
            ) : null}
          </p>
        </div>
        <Button
          type="button"
          variant="success"
          size="sm"
          onClick={onTakeBack}
          disabled={!canTakeBack}
          loading={held.pending}
        >
          <PhoneIncoming className="size-3.5" />
          {t("calls.waiting.takeBack")}
        </Button>
      </div>
      {note ? <p className="text-[12px] text-muted-foreground">{note}</p> : null}
    </div>
  );
}
